import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Upload, 
  Check, 
  X, 
  Clock, 
  Download,
  Eye,
  Edit,
  Save,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  FileCheck,
  GraduationCap,
  RotateCcw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useSync } from "../../contexts/SyncContext";
import { getAssessmentsForClass } from "../../data/mockAssessments";
import { getCurrentUser } from '@/data/users';
import apiService from '@/services/api';

export default function AssessmentSubmissionsPage() {
  const { studentDb, addChange, saveAssessmentMark, getStudentsByClass } = useSync();
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  
  // State for teacher's classes from API
  const [teacherClasses, setTeacherClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [selectedClass, setSelectedClass] = useState("");
  
  // Fetch teacher's classes from API
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        setLoading(true);
        const classes = await apiService.getTeacherClasses();
        setTeacherClasses(classes);
        
        // Set first class as selected
        if (classes.length > 0) {
          setSelectedClass(classes[0].name);
        }
      } catch (error) {
        console.error('Error fetching classes:', error);
        toast.error('Failed to load classes');
      } finally {
        setLoading(false);
      }
    };
    
    fetchClasses();
  }, []);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [submissions, setSubmissions] = useState({});
  const [markingDialogOpen, setMarkingDialogOpen] = useState(false);
  const [currentSubmission, setCurrentSubmission] = useState(null);
  const [marks, setMarks] = useState({});

  // Load assessments when class changes
  useEffect(() => {
    const classAssessments = getAssessmentsForClass(selectedClass);
    setAssessments(classAssessments);
    if (classAssessments.length > 0 && !selectedAssessment) {
      setSelectedAssessment(classAssessments[0]);
    }
  }, [selectedClass]);

  // Mock submission data - would come from backend
  useEffect(() => {
    const mockSubmissions = {};
    // Filter students by selected class
    const classStudents = getStudentsByClass(selectedClass);
    
    classStudents.forEach(student => {
      assessments.forEach(assessment => {
        const submissionId = `${student.id}-${assessment.id}`;
        mockSubmissions[submissionId] = {
          studentId: student.id,
          studentName: student.name,
          assessmentId: assessment.id,
          assessmentTitle: assessment.title,
          submittedAt: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000),
          status: Math.random() > 0.3 ? 'submitted' : 'pending',
          answers: {
            q1: Math.random() > 0.5 ? 'B' : 'A',
            q2: 'Sample answer about AI tutoring adapting to learning styles...',
            q3: Math.random() > 0.5 ? 'B' : 'A',
            q4: 'AI can provide remote diagnosis and consultation...',
            q5: Math.random() > 0.5 ? 'A' : 'B',
            q6: 'IoT connects devices for remote control...',
          },
          autoMarked: false,
          totalMarks: null,
          markedBy: null,
        };
      });
    });
    setSubmissions(mockSubmissions);
  }, [studentDb, assessments, selectedClass, getStudentsByClass]);

  // Answer keys for Unit 1.1 & 1.2 assessments
  const answerKeys = {
    'Y13-CS-CA1': { // Unit 1.1: Assessment Blueprint
      type: 'blueprint',
      totalMarks: 15,
      questions: [
        { id: 'q1', answer: 'B', marks: 2, type: 'mcq' },
        { id: 'q2', keywords: ['adapts', 'personalized', 'learning', 'pace'], marks: 3, type: 'short' },
        { id: 'q3', answer: 'B', marks: 2, type: 'mcq' },
        { id: 'q4', keywords: ['remote', 'diagnosis', 'consultation', 'AI'], marks: 3, type: 'short' },
        { id: 'q5', answer: 'A', marks: 2, type: 'mcq' },
        { id: 'q6', keywords: ['IoT', 'connects', 'devices', 'control', 'remote'], marks: 3, type: 'short' },
      ]
    },
    'Y13-CS-CA2': { // Unit 1.1: ICT Exercises
      type: 'exercises',
      totalMarks: 35,
      questions: [
        // Fill in blanks
        { id: 'q1', answer: 'Artificial Intelligence', marks: 1, type: 'fill' },
        { id: 'q2', answer: 'remotely', marks: 1, type: 'fill' },
        { id: 'q3', answer: 'Internet of Things', marks: 1, type: 'fill' },
        { id: 'q4', answer: '5G', marks: 1, type: 'fill' },
        { id: 'q5', answer: 'digital literacy', marks: 1, type: 'fill' },
        // Short answers
        { id: 'q6', keywords: ['AR', 'overlay', 'virtual', 'information', 'Vanuatu'], marks: 5, type: 'short' },
        { id: 'q7', keywords: ['AI', 'diagnose', 'remote', 'quickly', 'accurate'], marks: 5, type: 'short' },
        { id: 'q8', keywords: ['IoT', 'sensors', 'energy', 'efficient', 'monitor'], marks: 5, type: 'short' },
        // True/False
        { id: 'q9', answer: 'False', marks: 1, type: 'tf' },
        { id: 'q10', answer: 'True', marks: 1, type: 'tf' },
        { id: 'q11', answer: 'False', marks: 1, type: 'tf' },
        { id: 'q12', answer: 'False', marks: 1, type: 'tf' },
        { id: 'q13', answer: 'False', marks: 1, type: 'tf' },
        // Discussion
        { id: 'q14', keywords: ['internet', 'infrastructure', 'access', 'digital literacy', 'training', 'Vanuatu'], marks: 10, type: 'discussion' },
      ]
    },
    'Y13-CS-CA3': { // Unit 1.2: IS Management Test
      type: 'test',
      totalMarks: 60,
      questions: [
        // Multiple Choice
        { id: 'q1', answer: 'B', marks: 1, type: 'mcq' },
        { id: 'q2', answer: 'B', marks: 1, type: 'mcq' },
        { id: 'q3', answer: 'C', marks: 1, type: 'mcq' },
        { id: 'q4', answer: 'C', marks: 1, type: 'mcq' },
        { id: 'q5', answer: 'B', marks: 1, type: 'mcq' },
        { id: 'q6', answer: 'B', marks: 1, type: 'mcq' },
        { id: 'q7', answer: 'B', marks: 1, type: 'mcq' },
        { id: 'q8', answer: 'C', marks: 1, type: 'mcq' },
        { id: 'q9', answer: 'A', marks: 1, type: 'mcq' },
        { id: 'q10', answer: 'D', marks: 1, type: 'mcq' },
        // Fill in blanks
        { id: 'q11', answer: 'Input', marks: 2, type: 'fill' },
        { id: 'q12', answer: 'automate', marks: 2, type: 'fill' },
        { id: 'q13', answer: 'Student Information System', marks: 2, type: 'fill' },
        { id: 'q14', answer: 'Waterfall', marks: 2, type: 'fill' },
        { id: 'q15', answer: 'maintenance', marks: 2, type: 'fill' },
        // Short answers
        { id: 'q16', keywords: ['inputs', 'activities', 'steps', 'decisions', 'outputs', 'roles'], marks: 5, type: 'short' },
        { id: 'q17', keywords: ['automate', 'support', 'workflow', 'manage', 'efficient'], marks: 5, type: 'short' },
        { id: 'q18', keywords: ['booking', 'customer', 'online', 'reach', 'revenue'], marks: 5, type: 'short' },
        { id: 'q19', keywords: ['planning', 'analysis', 'design', 'implementation', 'maintenance'], marks: 5, type: 'short' },
        // Application
        { id: 'q20', keywords: ['booking', 'inquiry', 'workflow', 'Tourism Management', 'CRM'], marks: 10, type: 'short' },
        // Discussion
        { id: 'q21', keywords: ['infrastructure', 'connectivity', 'cost', 'skills', 'cultural', 'geographical', 'sustainability'], marks: 10, type: 'discussion' },
      ]
    },
    'Y13-CS-CA4': { // Unit 1.2: ICT Exercises
      type: 'exercises',
      totalMarks: 50,
      questions: [
        // Multiple Choice
        { id: 'q1', answer: 'Workflow', marks: 2, type: 'mcq' },
        { id: 'q2', answer: 'Improved student record management', marks: 2, type: 'mcq' },
        { id: 'q3', answer: 'Information Systems development', marks: 2, type: 'mcq' },
        { id: 'q4', answer: 'High costs', marks: 2, type: 'mcq' },
        { id: 'q5', answer: 'Managing an IS application', marks: 2, type: 'mcq' },
        // Fill in blanks
        { id: 'q6', answer: 'Input', marks: 2, type: 'fill' },
        { id: 'q7', answer: 'automate', marks: 2, type: 'fill' },
        { id: 'q8', answer: 'Student Information System', marks: 2, type: 'fill' },
        { id: 'q9', answer: 'Waterfall', marks: 2, type: 'fill' },
        { id: 'q10', answer: 'maintenance', marks: 2, type: 'fill' },
        // Short answers
        { id: 'q11', keywords: ['workflow', 'license', 'processing', 'application', 'approval'], marks: 3, type: 'short' },
        { id: 'q12', keywords: ['supports', 'automates', 'workflow', 'manages', 'data'], marks: 4, type: 'short' },
        { id: 'q13', keywords: ['booking', 'online', 'tourists', 'reach', 'revenue'], marks: 4, type: 'short' },
        { id: 'q14', keywords: ['planning', 'analysis', 'design', 'implementation'], marks: 2, type: 'short' },
        { id: 'q15', keywords: ['security', 'maintenance', 'support', 'training', 'data'], marks: 2, type: 'short' },
        // Discussion
        { id: 'q16', keywords: ['alignment', 'business', 'needs', 'improvement', 'efficiency', 'workflow'], marks: 15, type: 'discussion' },
      ]
    },
    'Y13-CS-CA5': { // Unit 1.2: ICT Assignment
      type: 'assignment',
      totalMarks: 60,
      questions: [
        // Definitions
        { id: 'q1', keywords: ['sequence', 'steps', 'goal', 'outcome', 'school', 'absence'], marks: 5, type: 'short' },
        { id: 'q2', keywords: ['creating', 'implementing', 'maintaining', 'Waterfall', 'sequential'], marks: 5, type: 'short' },
        { id: 'q3', keywords: ['collection', 'storage', 'organization', 'security', 'Vanuatu', 'decision'], marks: 5, type: 'short' },
        // Short answers
        { id: 'q4', keywords: ['supports', 'automates', 'workflow', 'Ministry', 'Education', 'salary'], marks: 10, type: 'short' },
        { id: 'q5', keywords: ['Student Record', 'Grading', 'Reporting', 'Communication', 'centralized'], marks: 9, type: 'short' },
        { id: 'q6', keywords: ['connectivity', 'infrastructure', 'IT', 'personnel', 'training', 'skills'], marks: 6, type: 'short' },
        // Scenario
        { id: 'q7', keywords: ['Tourism Management', 'booking', 'double-booking', 'customer', 'centralized', 'scheduling', 'training', 'support'], marks: 20, type: 'discussion' },
      ]
    },
    'Y13-CS-CA6': { // Unit 3.1: Data Representation Test
      type: 'test',
      totalMarks: 50,
      questions: [
        // Section 1: Digital Representation - Multiple Choice
        { id: 'q1', answer: 'B', marks: 1, type: 'mcq' },
        { id: 'q2', answer: 'B', marks: 1, type: 'mcq' },
        { id: 'q3', answer: 'A', marks: 1, type: 'mcq' },
        { id: 'q4', answer: 'C', marks: 1, type: 'mcq' },
        { id: 'q5', answer: 'D', marks: 1, type: 'mcq' },
        // Section 1: Digital Representation - Short Answer
        { id: 'q6', keywords: ['ASCII', 'limited', 'English', 'Unicode', 'universal', 'worldwide', 'languages'], marks: 3, type: 'short' },
        { id: 'q7', keywords: ['power', '2', 'base-16', 'nibble', '4 bits', 'direct', 'mapping'], marks: 3, type: 'short' },
        // Section 1: Digital Representation - Conversion
        { id: 'q8', answer: '27', marks: 2, type: 'fill' },
        { id: 'q9', answer: '110010', marks: 2, type: 'fill' },
        // Section 2: Binary Arithmetic - Problem Solving
        { id: 'q10', answer: '100011', marks: 4, type: 'fill' },
        { id: 'q11', answer: '1000', marks: 4, type: 'fill' },
        { id: 'q12', answer: '1110', marks: 4, type: 'fill' },
        // Section 2: Binary Arithmetic - True/False
        { id: 'q13', answer: 'False', marks: 1, type: 'tf' },
        { id: 'q14', answer: 'False', marks: 1, type: 'tf' },
        { id: 'q15', answer: 'True', marks: 1, type: 'tf' },
        // Section 3: Logic Gates - Matching (simplified to single answer)
        { id: 'q16', answer: 'a-iii-A, b-ii-C, c-i-B', marks: 5, type: 'fill' },
        // Section 3: Truth Table Completion
        { id: 'q17', answer: '0,1,1,1,1,1,0', marks: 5, type: 'fill' }, // NAND truth table
        // Section 3: Logic Circuit Analysis
        { id: 'q18', answer: '0,1,0,0', marks: 5, type: 'fill' },
        // Section 3: Short Answer
        { id: 'q19', keywords: ['OR', 'HIGH', 'at least one', 'alarm', 'sensor', 'door', 'window'], marks: 5, type: 'short' },
      ]
    },
    'Y13-CS-CA7': { // Unit 3.1: Assignment 1 - Digital Representation
      type: 'assignment',
      totalMarks: 30,
      questions: [
        { id: 'q1', keywords: ['ASCII', 'limited', 'English', 'regional', 'multilingual'], marks: 5, type: 'short' },
        { id: 'q2', answer: 'E5', marks: 3, type: 'fill' },
        { id: 'q3', answer: '10010110', marks: 3, type: 'fill' },
        { id: 'q4', answer: '63', marks: 3, type: 'fill' },
        { id: 'q5', answer: '1C', marks: 3, type: 'fill' },
        { id: 'q6', keywords: ['binary', 'ON', 'OFF', 'switches', 'Boolean', 'logic'], marks: 8, type: 'short' },
      ]
    },
    'Y13-CS-CA8': { // Unit 3.1: Assignment 2 - Binary Arithmetic
      type: 'assignment',
      totalMarks: 35,
      questions: [
        { id: 'q1', answer: '100000', marks: 5, type: 'fill' },
        { id: 'q2', answer: '0111', marks: 8, type: 'fill' },
        { id: 'q3', answer: '11110', marks: 8, type: 'fill' },
        { id: 'q4', answer: '1011', marks: 8, type: 'fill' },
        { id: 'q5', keywords: ['2s complement', 'addition', 'subtraction', 'circuitry', 'ALU'], marks: 6, type: 'short' },
      ]
    },
    'Y13-CS-CA9': { // Unit 3.1: Assignment 3 - Logic Gates
      type: 'assignment',
      totalMarks: 25,
      questions: [
        { id: 'q1', answer: '0,0,0,0,0,0,0,1', marks: 8, type: 'fill' }, // 3-input AND truth table
        { id: 'q2', answer: '0,1,0,0', marks: 8, type: 'fill' }, // NOT A AND B truth table
        { id: 'q3', keywords: ['NOR', 'gate', 'circuit'], marks: 9, type: 'short' },
      ]
    },
    'Y13-CS-CA10': { // Unit 3.1: Assignment 4 - Circuit Design
      type: 'assignment',
      totalMarks: 20,
      questions: [
        { id: 'q1', keywords: ['AND', 'gate', 'switches', 'series', 'both', 'HIGH'], marks: 10, type: 'short' },
        { id: 'q2', keywords: ['OR', 'gate', 'sensors', 'parallel', 'either', 'at least one'], marks: 10, type: 'short' },
      ]
    },
    'Y13-CS-CA11': { // Unit 3.2: Programming Concepts Test  
      type: 'test',
      totalMarks: 60,
      questions: [
        // Section 1: Understanding Programming - MCQ
        { id: 'q1', answer: 'B', marks: 1, type: 'mcq' },
        { id: 'q2', answer: 'C', marks: 1, type: 'mcq' },
        { id: 'q3', answer: 'D', marks: 1, type: 'mcq' },
        { id: 'q4', answer: 'B', marks: 1, type: 'mcq' },
        { id: 'q5', answer: 'C', marks: 1, type: 'mcq' },
        // Section 1: Short Answer
        { id: 'q6', keywords: ['instructions', 'computers', 'tasks', 'problems', 'Vanuatu', 'applications'], marks: 5, type: 'short' },
        { id: 'q7', keywords: ['Program Design', 'planning', 'algorithms', 'flowcharts', 'Program Documentation', 'written', 'explain', 'purpose', 'users'], marks: 5, type: 'short' },
        // Section 2: Problem Solving - Matching
        { id: 'q8', answer: 'a-ii', marks: 1, type: 'fill' },
        { id: 'q9', answer: 'b-i', marks: 1, type: 'fill' },
        { id: 'q10', answer: 'c-iii', marks: 1, type: 'fill' },
        { id: 'q11', answer: 'd-v', marks: 1, type: 'fill' },
        { id: 'q12', answer: 'e-iv', marks: 1, type: 'fill' },
        // Section 2: Short Answer
        { id: 'q13', keywords: ['top-down', 'complex system', 'student information', 'main functionalities', 'sub-tasks', 'manage complexity'], marks: 5, type: 'short' },
        { id: 'q14', keywords: ['identifying', 'need', 'opportunity', 'categorization', 'type', 'components', 'inventory', 'stock', 'orders'], marks: 5, type: 'short' },
        // Section 3: Pseudocode Analysis
        { id: 'q15', keywords: ['input', 'numbers', 'compares', 'greater', 'outputs', 'larger'], marks: 7, type: 'short' },
        // Section 3: Flow Diagram
        { id: 'q16', keywords: ['START', 'Input Age', 'Age >= 18', 'decision', 'eligible to vote', 'not eligible', 'END'], marks: 8, type: 'short' },
        // Section 4: Coding
        { id: 'q17', keywords: ['writing', 'instructions', 'programming language', 'computer', 'execute', 'tasks'], marks: 4, type: 'short' },
        { id: 'q18', keywords: ['organization', 'readability', 'modules', 'reusability', 'save time'], marks: 4, type: 'short' },
        // Section 4: Code Analysis
        { id: 'q19', keywords: ['functions', 'calculateArea', 'calculatePerimeter', 'encapsulates', 'organization', 'reusability', 'readability'], marks: 7, type: 'short' },
      ]
    },
    'Y13-CS-CA12': { // Unit 3.3: Microprocessors and Embedded Systems Test
      type: 'test',
      totalMarks: 70,
      questions: [
        // Section 1: Microprocessor Architecture - MCQ
        { id: 'q1', answer: 'C', marks: 1, type: 'mcq' },
        { id: 'q2', answer: 'C', marks: 1, type: 'mcq' },
        { id: 'q3', answer: 'B', marks: 1, type: 'mcq' },
        { id: 'q4', answer: 'C', marks: 1, type: 'mcq' },
        { id: 'q5', answer: 'B', marks: 1, type: 'mcq' },
        // Section 1: Short Answer
        { id: 'q6', keywords: ['arithmetic', 'logical', 'operations', 'addition', 'subtraction', 'AND', 'OR', 'comparisons'], marks: 5, type: 'short' },
        { id: 'q7', keywords: ['communication', 'channel', 'microprocessor', 'memory', 'Address Bus', 'Data Bus', 'Control Bus', 'peripherals'], marks: 5, type: 'short' },
        { id: 'q8', keywords: ['Control Unit', 'fetches', 'decodes', 'ALU', 'calculations', 'Registers', 'storage', 'coordinated', 'execution'], marks: 5, type: 'short' },
        // Section 2: From Code to Execution - Matching
        { id: 'q9', answer: 'a-iv', marks: 1, type: 'fill' },
        { id: 'q10', answer: 'b-i', marks: 1, type: 'fill' },
        { id: 'q11', answer: 'c-v', marks: 1, type: 'fill' },
        { id: 'q12', answer: 'd-iii', marks: 1, type: 'fill' },
        { id: 'q13', answer: 'e-ii', marks: 1, type: 'fill' },
        // Section 2: Short Answer
        { id: 'q14', keywords: ['high-level', 'program', 'compiler', 'interpreter', 'machine code', 'translated', 'execute'], marks: 5, type: 'short' },
        { id: 'q15', keywords: ['assembler', 'assembly language', 'machine code', 'mnemonics', 'low-level', 'embedded', 'hardware', 'performance'], marks: 5, type: 'short' },
        // Section 3: Programming and Interfacing - Short Answer
        { id: 'q16', keywords: ['Read', 'switch', 'input', 'closed', 'HIGH', 'LED', 'ON', 'output', 'open', 'LOW', 'OFF'], marks: 5, type: 'short' },
        { id: 'q17', keywords: ['sensors', 'detect', 'physical', 'temperature', 'light', 'actuators', 'response', 'motor', 'fan', 'alarm'], marks: 5, type: 'short' },
        { id: 'q18', keywords: ['temperature sensor', 'analog', 'voltage', 'ADC', 'Analog-to-Digital', 'digital', 'microprocessor', 'process'], marks: 5, type: 'short' },
        // Section 4: Embedded Systems - MCQ
        { id: 'q19', answer: 'B', marks: 1, type: 'mcq' },
        { id: 'q20', answer: 'C', marks: 1, type: 'mcq' },
        { id: 'q21', answer: 'C', marks: 1, type: 'mcq' },
        { id: 'q22', answer: 'C', marks: 1, type: 'mcq' },
        { id: 'q23', answer: 'C', marks: 1, type: 'mcq' },
        // Section 4: Short Answer
        { id: 'q24', keywords: ['embedded system', 'specialized', 'dedicated', 'function', 'mobile phones', 'solar inverters', 'digital television', 'Vanuatu'], marks: 5, type: 'short' },
        { id: 'q25', keywords: ['programmable', 'flexibility', 'software', 'automation', 'tasks', 'adaptability', 'customized', 'control'], marks: 5, type: 'short' },
        { id: 'q26', keywords: ['software errors', 'medical', 'vital signs', 'inaccurate', 'readings', 'diagnosis', 'malfunction', 'life-threatening', 'patient'], marks: 5, type: 'short' },
      ]
    },
    'Y13-CS-CA13': { // Unit 3.3: Assignment 1 - Inner Workings of Microprocessor
      type: 'assignment',
      totalMarks: 20,
      questions: [
        { id: 'q1', keywords: ['Control Unit', 'fetches', 'instruction', 'memory', 'decodes', 'Address Bus', 'Data Bus', 'ALU', 'addition', 'Register A', 'result'], marks: 10, type: 'short' },
        { id: 'q2', keywords: ['fetch instructions', 'program code', 'access data', 'read', 'write', 'store results', 'system bus', 'communication'], marks: 5, type: 'short' },
        { id: 'q3', keywords: ['Control Unit', 'brain', 'fetches', 'decodes', 'executes', 'directs', 'ALU', 'workhorse', 'arithmetic', 'logical', 'calculations'], marks: 5, type: 'short' },
      ]
    },
    'Y13-CS-CA14': { // Unit 3.3: Assignment 2 - High-Level to Machine Language
      type: 'assignment',
      totalMarks: 15,
      questions: [
        { id: 'q1', keywords: ['Lexical Analysis', 'Scanning', 'tokens', 'Syntax Analysis', 'Parsing', 'grammar', 'parse tree', 'Semantic Analysis', 'type checking', 'Intermediate Code', 'Optimization', 'efficiency', 'Code Generation', 'machine code'], marks: 10, type: 'short' },
        { id: 'q2', keywords: ['assembler', 'translates', 'assembly language', 'machine code', 'hardware control', 'performance', 'optimization', 'embedded', 'bootloaders', 'low-level'], marks: 5, type: 'short' },
      ]
    },
    'Y13-CS-CA15': { // Unit 3.3: Assignment 3 - Programming Traffic Light System
      type: 'assignment',
      totalMarks: 15,
      questions: [
        { id: 'q1', keywords: ['Green', 'ON', 'wait', '3 seconds', 'OFF', 'Yellow', '1 second', 'Red', '2 seconds', 'repeat', 'cycle'], marks: 8, type: 'short' },
        { id: 'q2', keywords: ['time delays', 'Wait', 'pause', 'duration', 'seconds', 'milliseconds', 'timer', 'execution', 'delay function'], marks: 7, type: 'short' },
      ]
    },
    'Y13-CS-CA16': { // Unit 3.3: Assignment 4 - Embedded Systems in Vanuatu
      type: 'assignment',
      totalMarks: 15,
      questions: [
        { id: 'q1', keywords: ['embedded device', 'computer system', 'integrated', 'dedicated function', 'Solar Power', 'Mobile Phone', 'Electronic Voting', 'Vanuatu'], marks: 6, type: 'short' },
        { id: 'q2', keywords: ['microprocessor', 'reads', 'voltage', 'current', 'sensors', 'controls', 'energy flow', 'battery', 'charge', 'algorithm', 'prevents', 'overcharging', 'monitors'], marks: 5, type: 'short' },
        { id: 'q3', keywords: ['economic benefit', 'Precision Agriculture', 'sensors', 'monitor', 'soil', 'weather', 'optimize', 'irrigation', 'fertilization', 'yields', 'income', 'sustainable', 'Vanuatu'], marks: 4, type: 'short' },
      ]
    },
    'Y13-CS-CA17': { // Unit 3.3: Assignment 5 - Software Reliability (Optional)
      type: 'assignment',
      totalMarks: 10,
      questions: [
        { id: 'q1', keywords: ['software errors', 'blood pressure', 'inaccurate readings', 'wrong diagnosis', 'harmful treatment', 'failure', 'malfunction', 'missed', 'critical', 'emergencies'], marks: 5, type: 'short' },
        { id: 'q2', keywords: ['Safety', 'prevents harm', 'users', 'environment', 'medical devices', 'vehicles', 'Reliability', 'consistent', 'predictable', 'trust', 'critical'], marks: 5, type: 'short' },
      ]
    },
    'Y13-CS-CA18': { // Unit 4.1: ICT Security Exercises
      type: 'exercises',
      totalMarks: 50,
      questions: [
        // Section 1: Definitions (4 marks)
        { id: 'q1', answer: 'b', marks: 2, type: 'mcq' },
        { id: 'q2', answer: 'c', marks: 2, type: 'mcq' },
        { id: 'q3', answer: 'hackers', marks: 2, type: 'fill' },
        { id: 'q4', answer: 'communications', marks: 2, type: 'fill' },
        
        // Section 2: Cybercrime Concerns (7 marks)
        { id: 'q5', answer: 'true', marks: 1, type: 'tf' },
        { id: 'q6', answer: 'false', marks: 1, type: 'tf' },
        { id: 'q7', keywords: ['ransomware', 'data breaches', 'phishing', 'cyberbullying', 'online defamation', 'attacks'], marks: 3, type: 'short' },
        { id: 'q8', keywords: ['lack', 'capacity', 'training', 'resources', 'technical', 'awareness'], marks: 2, type: 'short' },
        
        // Section 3: Cyber Legislation (5 marks)
        { id: 'q9', answer: 'b', marks: 2, type: 'mcq' },
        { id: 'q10', keywords: ['offenses', 'penalties', 'punishments', 'definitions', 'legal framework'], marks: 3, type: 'short' },
        
        // Section 4: Reported Cybercrimes (4 marks)
        { id: 'q11', answer: 'true', marks: 1, type: 'tf' },
        { id: 'q12', answer: 'false', marks: 1, type: 'tf' },
        { id: 'q13', answer: 'slander', marks: 1, type: 'fill' },
        { id: 'q14', answer: 'online defamation', marks: 1, type: 'fill' },
        
        // Section 5: Combating Cybercrimes (5 marks)
        { id: 'q15', answer: 'b', marks: 2, type: 'mcq' },
        { id: 'q16', keywords: ['CERT', 'Computer Emergency Response Team', 'Vanuatu', 'collaborations', 'international'], marks: 3, type: 'short' },
        
        // Section 6: Challenges for Pacific Nations (5 marks)
        { id: 'q17', answer: 'b', marks: 2, type: 'mcq' },
        { id: 'q18', answer: 'false', marks: 1, type: 'tf' },
        { id: 'q19', answer: 'true', marks: 1, type: 'tf' },
        { id: 'q20', keywords: ['legislation', 'technical capacity', 'resources', 'awareness', 'geographical', 'internet'], marks: 1, type: 'short' },
        
        // Section 7: Preventative Plans (4 marks)
        { id: 'q21', answer: 'strong', marks: 1, type: 'fill' },
        { id: 'q22', answer: 'updating', marks: 1, type: 'fill' },
        { id: 'q23', keywords: ['passwords', 'updates', 'antivirus', 'software', 'backups', 'education'], marks: 2, type: 'short' },
        
        // Section 8: Cyber Security Attacks (4 marks)
        { id: 'q24', answer: 'c', marks: 2, type: 'mcq' },
        { id: 'q25', answer: 'c', marks: 2, type: 'mcq' },
        { id: 'q26', answer: 'malicious software', marks: 1, type: 'fill' },
        { id: 'q27', answer: 'denial-of-service', marks: 1, type: 'fill' },
        
        // Section 9: Threats to ICT Systems (6 marks)
        { id: 'q28', answer: 'true', marks: 1, type: 'tf' },
        { id: 'q29', answer: 'false', marks: 1, type: 'tf' },
        { id: 'q30', keywords: ['unauthorized access', 'data theft', 'malware', 'deployment', 'information'], marks: 2, type: 'short' },
        { id: 'q31', keywords: ['ransomware', 'attack', 'hospital', 'infrastructure', 'financial gain', 'motive'], marks: 2, type: 'short' },
        
        // Section 10: Effects and Impacts (3 marks)
        { id: 'q32', answer: 'c', marks: 2, type: 'mcq' },
        { id: 'q33', answer: 'disruption', marks: 1, type: 'fill' },
        { id: 'q34', answer: 'reputational', marks: 1, type: 'fill' },
        
        // Section 11: Identity Theft (4 marks)
        { id: 'q35', answer: 'true', marks: 1, type: 'tf' },
        { id: 'q36', answer: 'false', marks: 1, type: 'tf' },
        { id: 'q37', keywords: ['financial loss', 'credit rating', 'personal information', 'misuse', 'identity'], marks: 2, type: 'short' },
        
        // Section 12: Viruses and Malware (3 marks)
        { id: 'q38', answer: 'c', marks: 2, type: 'mcq' },
        { id: 'q39', answer: 'malware', marks: 1, type: 'fill' },
        { id: 'q40', answer: 'corrupt', marks: 1, type: 'fill' },
        
        // Section 13: Online Identity Issues (4 marks)
        { id: 'q41', answer: 'c', marks: 2, type: 'mcq' },
        { id: 'q42', keywords: ['privacy breaches', 'identity theft', 'cyberstalking', 'reputational damage', 'personal information'], marks: 2, type: 'short' },
        
        // Section 14: Cybersecurity Guidelines (4 marks)
        { id: 'q43', answer: 'b', marks: 2, type: 'mcq' },
        { id: 'q44', answer: 'password', marks: 1, type: 'fill' },
        { id: 'q45', answer: 'multi-factor', marks: 1, type: 'fill' },
        { id: 'q46', keywords: ['password policies', 'software updates', 'antivirus', 'firewall', 'backup'], marks: 2, type: 'short' }
      ]
    },
    'Y13-CS-CA19': { // Unit 4.1: Cybercrime & Cybersecurity Test (100 marks)
      type: 'test',
      totalMarks: 100,
      questions: [
        // Section I: Definitions and Concepts - MCQ (10 marks)
        { id: 'q1', answer: 'b', marks: 2, type: 'mcq' },
        { id: 'q2', answer: 'b', marks: 2, type: 'mcq' },
        { id: 'q3', answer: 'c', marks: 2, type: 'mcq' },
        { id: 'q4', answer: 'c', marks: 2, type: 'mcq' },
        { id: 'q5', answer: 'c', marks: 2, type: 'mcq' },
        
        // Section I: Definitions and Concepts - Fill-in-blank (10 marks)
        { id: 'q6', answer: 'cybercrime', marks: 2, type: 'fill' },
        { id: 'q7', answer: 'cyber legislation', marks: 2, type: 'fill' },
        { id: 'q8', answer: 'phishing', marks: 2, type: 'fill' },
        { id: 'q9', answer: 'spyware', marks: 2, type: 'fill' },
        { id: 'q10', answer: 'malware', marks: 2, type: 'fill' },
        
        // Section II: Cybercrime Landscape - True/False (5 marks)
        { id: 'q11', answer: 'false', marks: 1, type: 'tf' },
        { id: 'q12', answer: 'true', marks: 1, type: 'tf' },
        { id: 'q13', answer: 'false', marks: 1, type: 'tf' },
        { id: 'q14', answer: 'true', marks: 1, type: 'tf' },
        { id: 'q15', answer: 'false', marks: 1, type: 'tf' },
        
        // Section II: Cybercrime Landscape - Short Answer (20 marks)
        { id: 'q16', keywords: ['social media', 'cyberbullying', 'online defamation', 'limited resources', 'vulnerability', 'attacks', 'institutions'], marks: 5, type: 'short' },
        { id: 'q17', keywords: ['image abuse', 'slander', 'cyberbullying', 'ATM fraud', 'forgery', 'cyber stalking', 'computer fraud', 'online defamation'], marks: 4, type: 'short' },
        { id: 'q18', keywords: ['employee', 'malicious link', 'malware', 'leak', 'data', 'organized crime', 'ransomware', 'infrastructure', 'financial gain', 'illicit communication'], marks: 6, type: 'short' },
        { id: 'q19', keywords: ['inadequate', 'legislation', 'hinders', 'effective', 'prosecution', 'deterrence', 'cybercriminals'], marks: 5, type: 'short' },
        
        // Section III: Legal Measures - MCQ (10 marks)
        { id: 'q20', answer: 'b', marks: 2, type: 'mcq' },
        { id: 'q21', answer: 'b', marks: 2, type: 'mcq' },
        { id: 'q22', answer: 'b', marks: 2, type: 'mcq' },
        { id: 'q23', answer: 'c', marks: 2, type: 'mcq' },
        { id: 'q24', answer: 'c', marks: 2, type: 'mcq' },
        
        // Section III: Legal Measures - Short Answer (15 marks)
        { id: 'q25', keywords: ['definitions', 'offenses', 'penalties', 'punishments', 'legal framework', 'consequences'], marks: 5, type: 'short' },
        { id: 'q26', keywords: ['strong passwords', 'multi-factor authentication', 'updating software', 'antivirus', 'cautious', 'phishing'], marks: 4, type: 'short' },
        { id: 'q27', keywords: ['backups', 'restoration', 'critical information', 'cyber-attack', 'system failure', 'data loss', 'business continuity'], marks: 6, type: 'short' },
        
        // Section IV: Challenges and Impacts - Short Answer (10 marks)
        { id: 'q28', keywords: ['legislation', 'understanding', 'technical capacity', 'expertise', 'resources', 'constraints', 'infrastructure'], marks: 5, type: 'short' },
        { id: 'q29', keywords: ['disruption', 'services', 'revenue', 'theft', 'financial', 'customer data', 'reputational damage', 'recovery costs'], marks: 5, type: 'short' },
        
        // Section IV: Challenges and Impacts - Essay (10 marks)
        { id: 'q30', keywords: ['challenges', 'Pacific', 'legislation', 'enforcement', 'technical expertise', 'training', 'resources', 'infrastructure', 'cooperation', 'Vanuatu', 'financial impacts', 'operational impacts', 'reputation', '2022', 'ransomware', 'government', 'disruption', 'vulnerability'], marks: 10, type: 'essay' },
        
        // Section V: Cybersecurity Guidelines - Fill-in-blank (5 marks)
        { id: 'q31', answer: 'password', marks: 1, type: 'fill' },
        { id: 'q32', answer: 'updating', marks: 1, type: 'fill' },
        { id: 'q33', answer: 'awareness', marks: 1, type: 'fill' },
        { id: 'q34', answer: 'access', marks: 1, type: 'fill' },
        { id: 'q35', answer: 'wipe', marks: 1, type: 'fill' },
        
        // Section V: Cybersecurity Guidelines - Short Answer (5 marks)
        { id: 'q36', keywords: ['password policies', 'complex passwords', 'regular changes', 'unauthorized', 'brute-force', 'entry point', 'cybercriminals'], marks: 5, type: 'short' }
      ]
    },
    'Y13-CS-CA20': { // Unit 4.2: Ethical Measures in ICT Test (100 marks)
      type: 'test',
      totalMarks: 100,
      questions: [
        // Section I: Definitions - MCQ (10 marks)
        { id: 'q1', answer: 'b', marks: 2, type: 'mcq' },
        { id: 'q2', answer: 'b', marks: 2, type: 'mcq' },
        { id: 'q3', answer: 'c', marks: 2, type: 'mcq' },
        { id: 'q4', answer: 'c', marks: 2, type: 'mcq' },
        { id: 'q5', answer: 'c', marks: 2, type: 'mcq' },
        
        // Section I: Definitions - Fill-in-blank (10 marks)
        { id: 'q6', answer: 'cybercrime', marks: 2, type: 'fill' },
        { id: 'q7', answer: 'cyber legislation', marks: 2, type: 'fill' },
        { id: 'q8', answer: 'phishing', marks: 2, type: 'fill' },
        { id: 'q9', answer: 'spyware', marks: 2, type: 'fill' },
        { id: 'q10', answer: 'malware', marks: 2, type: 'fill' },
        
        // Section II: Landscape - True/False (5 marks)
        { id: 'q11', answer: 'false', marks: 1, type: 'tf' },
        { id: 'q12', answer: 'true', marks: 1, type: 'tf' },
        { id: 'q13', answer: 'false', marks: 1, type: 'tf' },
        { id: 'q14', answer: 'true', marks: 1, type: 'tf' },
        { id: 'q15', answer: 'false', marks: 1, type: 'tf' },
        
        // Section II: Landscape - Short Answer (20 marks)
        { id: 'q16', keywords: ['social media', 'cyberbullying', 'online defamation', 'limited resources', 'vulnerability', 'institutions', 'attacks'], marks: 5, type: 'short' },
        { id: 'q17', keywords: ['image abuse', 'slander', 'cyberbullying', 'ATM fraud', 'forgery', 'cyber stalking', 'fraud', 'defamation'], marks: 5, type: 'short' },
        { id: 'q18', keywords: ['employee', 'malicious link', 'malware', 'leak', 'data', 'organized crime', 'ransomware', 'infrastructure'], marks: 5, type: 'short' },
        { id: 'q19', keywords: ['inadequate', 'legislation', 'lack', 'understanding'], marks: 5, type: 'short' },
        
        // Section III: Legal Measures - MCQ (10 marks)
        { id: 'q20', answer: 'b', marks: 2, type: 'mcq' },
        { id: 'q21', answer: 'b', marks: 2, type: 'mcq' },
        { id: 'q22', answer: 'b', marks: 2, type: 'mcq' },
        { id: 'q23', answer: 'c', marks: 2, type: 'mcq' },
        { id: 'q24', answer: 'c', marks: 2, type: 'mcq' },
        
        // Section III: Legal Measures - Short Answer (15 marks)
        { id: 'q25', keywords: ['definitions', 'offenses', 'penalties', 'punishments', 'legal framework'], marks: 5, type: 'short' },
        { id: 'q26', keywords: ['strong passwords', 'multi-factor', 'authentication', 'updating', 'software', 'antivirus', 'phishing'], marks: 5, type: 'short' },
        { id: 'q27', keywords: ['backups', 'restoration', 'critical information', 'cyber-attack', 'data loss', 'recovery'], marks: 5, type: 'short' },
        
        // Section IV: Challenges - Short Answer (10 marks)
        { id: 'q28', keywords: ['legislation', 'understanding', 'technical capacity', 'expertise', 'resources', 'constraints'], marks: 5, type: 'short' },
        { id: 'q29', keywords: ['disruption', 'services', 'revenue', 'financial', 'theft', 'data', 'reputational'], marks: 5, type: 'short' },
        
        // Section IV: Essay (10 marks)
        { id: 'q30', keywords: ['challenges', 'Pacific', 'legislation', 'enforcement', 'technical', 'resources', 'cooperation', 'Vanuatu', 'ransomware', 'impacts'], marks: 10, type: 'essay' },
        
        // Section V: Guidelines - Fill-in-blank (5 marks)
        { id: 'q31', answer: 'password', marks: 1, type: 'fill' },
        { id: 'q32', answer: 'updating', marks: 1, type: 'fill' },
        { id: 'q33', answer: 'awareness', marks: 1, type: 'fill' },
        { id: 'q34', answer: 'access', marks: 1, type: 'fill' },
        { id: 'q35', answer: 'wipe', marks: 1, type: 'fill' },
        
        // Section V: Guidelines - Short Answer (5 marks)
        { id: 'q36', keywords: ['password policies', 'complex', 'unauthorized', 'cybersecurity', 'guidelines'], marks: 5, type: 'short' }
      ]
    },
    'Y13-CS-CA21': { // Unit 4.2: Ethical Measures Exercises (50 marks)
      type: 'exercises',
      totalMarks: 50,
      questions: [
        // Section 1: Definitions (4 marks)
        { id: 'q1', answer: 'b', marks: 2, type: 'mcq' },
        { id: 'q2', answer: 'c', marks: 2, type: 'mcq' },
        { id: 'q3', answer: 'hackers', marks: 1, type: 'fill' },
        { id: 'q4', answer: 'communications', marks: 1, type: 'fill' },
        
        // Section 2: Concerns (7 marks)
        { id: 'q5', answer: 'true', marks: 1, type: 'tf' },
        { id: 'q6', answer: 'false', marks: 1, type: 'tf' },
        { id: 'q7', keywords: ['ransomware', 'data breaches', 'phishing', 'cyberbullying'], marks: 3, type: 'short' },
        { id: 'q8', keywords: ['lack', 'capacity', 'training', 'resources'], marks: 2, type: 'short' },
        
        // Section 3: Legislation (5 marks)
        { id: 'q9', answer: 'b', marks: 2, type: 'mcq' },
        { id: 'q10', keywords: ['offenses', 'penalties', 'definitions'], marks: 3, type: 'short' },
        { id: 'q11', keywords: ['definitions', 'penalties', 'punishments'], marks: 3, type: 'short' },
        
        // Section 4: Reported Crimes (4 marks)
        { id: 'q12', answer: 'true', marks: 1, type: 'tf' },
        { id: 'q13', answer: 'false', marks: 1, type: 'tf' },
        { id: 'q14', answer: 'slander', marks: 1, type: 'fill' },
        { id: 'q15', answer: 'defamation', marks: 1, type: 'fill' },
        
        // Section 5: Efforts (5 marks)
        { id: 'q16', answer: 'b', marks: 2, type: 'mcq' },
        { id: 'q17', keywords: ['CERT', 'Computer Emergency Response Team', 'collaborations', 'international'], marks: 3, type: 'short' },
        { id: 'q18', keywords: ['strengthening', 'legal', 'capacity building', 'awareness', 'training'], marks: 3, type: 'short' },
        
        // Section 6: Challenges (7 marks)
        { id: 'q19', answer: 'b', marks: 2, type: 'mcq' },
        { id: 'q20', answer: 'false', marks: 1, type: 'tf' },
        { id: 'q21', answer: 'true', marks: 1, type: 'tf' },
        { id: 'q22', keywords: ['legislation', 'technical capacity', 'resources'], marks: 2, type: 'short' },
        { id: 'q23', keywords: ['lack', 'comprehensive', 'cybercrime', 'laws', 'enforce'], marks: 1, type: 'short' },
        
        // Section 7: Prevention (6 marks)
        { id: 'q24', answer: 'strong', marks: 1, type: 'fill' },
        { id: 'q25', answer: 'updating', marks: 1, type: 'fill' },
        { id: 'q26', keywords: ['passwords', 'updates', 'antivirus', 'backups'], marks: 2, type: 'short' },
        { id: 'q27', keywords: ['education', 'awareness', 'training', 'threats', 'safe practices'], marks: 2, type: 'short' }
      ]
    },
    'Y13-CS-CA22': { // Unit 4.3: Environmental Impact of ICT Test (100 marks)
      type: 'test',
      totalMarks: 100,
      questions: [
        // Section I: Introduction (10 MCQs + 5 Fill-blanks = 20 marks)
        { id: 'q1', answer: 'c', marks: 2, type: 'mcq' },
        { id: 'q2', answer: 'b', marks: 2, type: 'mcq' },
        { id: 'q3', answer: 'c', marks: 2, type: 'mcq' },
        { id: 'q4', answer: 'c', marks: 2, type: 'mcq' },
        { id: 'q5', answer: 'b', marks: 2, type: 'mcq' },
        { id: 'q6', answer: 'e-waste', marks: 2, type: 'fill' },
        { id: 'q7', answer: 'lead', marks: 2, type: 'fill' },
        { id: 'q8', answer: 'dioxins', marks: 2, type: 'fill' },
        { id: 'q9', answer: 'carbon footprint', marks: 2, type: 'fill' },
        { id: 'q10', answer: 'land', marks: 2, type: 'fill' },
        
        // Section II: Disposal (5 TF + 4 Short = 25 marks)
        { id: 'q11', answer: 'false', marks: 1, type: 'tf' },
        { id: 'q12', answer: 'true', marks: 1, type: 'tf' },
        { id: 'q13', answer: 'false', marks: 1, type: 'tf' },
        { id: 'q14', answer: 'true', marks: 1, type: 'tf' },
        { id: 'q15', answer: 'false', marks: 1, type: 'tf' },
        { id: 'q16', keywords: ['leaching', 'toxic', 'contamination', 'soil', 'groundwater'], marks: 5, type: 'short' },
        { id: 'q17', keywords: ['dioxins', 'furans', 'heavy metals', 'toxic', 'air pollution', 'health'], marks: 5, type: 'short' },
        { id: 'q18', keywords: ['limited land', 'fragile', 'ecosystems', 'resources', 'infrastructure', 'expensive'], marks: 5, type: 'short' },
        { id: 'q19', keywords: ['lead', 'mercury', 'cadmium', 'water', 'soil', 'contamination'], marks: 5, type: 'short' },
        
        // Section III: Electricity (4 MCQs + 2 Short = 20 marks)
        { id: 'q20', answer: 'b', marks: 2, type: 'mcq' },
        { id: 'q21', answer: 'b', marks: 2, type: 'mcq' },
        { id: 'q22', answer: 'c', marks: 2, type: 'mcq' },
        { id: 'q23', answer: 'c', marks: 2, type: 'mcq' },
        { id: 'q24', answer: 'c', marks: 2, type: 'mcq' },
        { id: 'q25', keywords: ['greenhouse gas', 'emissions', 'fossil fuels', 'climate change'], marks: 5, type: 'short' },
        { id: 'q26', keywords: ['electricity', 'fossil fuels', 'CO2', 'greenhouse', 'warming'], marks: 5, type: 'short' },
        
        // Section IV: Management (4 Fill-blanks + 3 Short = 20 marks)
        { id: 'q27', answer: 'collection', marks: 1, type: 'fill' },
        { id: 'q28', answer: 'reusing', marks: 1, type: 'fill' },
        { id: 'q29', answer: 'recycling', marks: 1, type: 'fill' },
        { id: 'q30', answer: 'Extended', marks: 1, type: 'fill' },
        { id: 'q31', answer: 'infrastructure', marks: 1, type: 'fill' },
        { id: 'q32', keywords: ['collection points', 'repair', 'partnerships', 'awareness', 'effective'], marks: 5, type: 'short' },
        { id: 'q33', keywords: ['EPR', 'recycling', 'producer', 'responsibility', 'materials', 'recovery'], marks: 5, type: 'short' },
        { id: 'q34', keywords: ['toxic', 'leaching', 'resources', 'wasted', 'valuable', 'materials'], marks: 5, type: 'short' },
        
        // Section V: Sustainable (3 MCQs + 2 Short = 15 marks)
        { id: 'q35', answer: 'c', marks: 2, type: 'mcq' },
        { id: 'q36', answer: 'c', marks: 2, type: 'mcq' },
        { id: 'q37', answer: 'c', marks: 2, type: 'mcq' },
        { id: 'q38', keywords: ['environment', 'social', 'economic', 'future', 'generations', 'responsible'], marks: 3, type: 'short' },
        { id: 'q39', keywords: ['solar', 'energy-efficient', 'renewable', 'recycling', 'sustainable'], marks: 3, type: 'short' },
        { id: 'q40', keywords: ['circular economy', 'EPR', 'recycling', 'renewable', 'design', 'sustainability'], marks: 3, type: 'short' }
      ]
    },
    'Y13-CS-CA23': { // Unit 4.3: Environmental Impact Exercises (50 marks)
      type: 'exercises',
      totalMarks: 50,
      questions: [
        { id: 'q1', answer: 'c', marks: 2, type: 'mcq' },
        { id: 'q2', answer: 'false', marks: 1, type: 'tf' },
        { id: 'q3', answer: 'true', marks: 1, type: 'tf' },
        { id: 'q4', answer: 'resources', marks: 1, type: 'fill' },
        { id: 'q5', answer: 'c', marks: 2, type: 'mcq' },
        { id: 'q6', keywords: ['toxic', 'fumes', 'air', 'contamination', 'greenhouse'], marks: 2, type: 'short' },
        { id: 'q7', answer: 'false', marks: 1, type: 'tf' },
        { id: 'q8', answer: 'true', marks: 1, type: 'tf' },
        { id: 'q9', answer: 'carbon footprint', marks: 1, type: 'fill' },
        { id: 'q10', answer: 'c', marks: 2, type: 'mcq' },
        { id: 'q11', keywords: ['tourism', 'pollution', 'fisheries', 'livelihoods', 'food security'], marks: 2, type: 'short' },
        { id: 'q12', answer: 'false', marks: 1, type: 'tf' },
        { id: 'q13', answer: 'true', marks: 1, type: 'tf' },
        { id: 'q14', answer: 'repair', marks: 1, type: 'fill' },
        { id: 'q15', answer: 'b', marks: 2, type: 'mcq' },
        { id: 'q16', keywords: ['environmentally', 'sound', 'materials', 'recovery', 'disposal'], marks: 2, type: 'short' },
        { id: 'q17', answer: 'false', marks: 1, type: 'tf' },
        { id: 'q18', answer: 'true', marks: 1, type: 'tf' },
        { id: 'q19', answer: 'Virtualization', marks: 1, type: 'fill' },
        { id: 'q20', answer: 'b', marks: 2, type: 'mcq' },
        { id: 'q21', keywords: ['costs', 'carbon footprint', 'operational', 'public image'], marks: 2, type: 'short' },
        { id: 'q22', answer: 'false', marks: 1, type: 'tf' },
        { id: 'q23', answer: 'true', marks: 1, type: 'tf' },
        { id: 'q24', answer: 'circular', marks: 1, type: 'fill' },
        { id: 'q25', keywords: ['sustainability', 'reuse', 'refurbishment', 'recycling', 'renewable'], marks: 2, type: 'short' },
        { id: 'q26', keywords: ['circular economy', 'renewable energy', 'energy-efficient', 'recycling'], marks: 2, type: 'short' }
      ]
    }
  };

  const autoMarkSubmission = (submission, assessment) => {
    if (!answerKeys[assessment.id]) return null;

    const answerKey = answerKeys[assessment.id];
    let totalMarks = 0;
    const questionMarks = {};

    answerKey.questions.forEach(q => {
      const studentAnswer = submission.answers[q.id];
      
      if (q.type === 'mcq' || q.type === 'fill' || q.type === 'tf') {
        // Exact match for objective questions
        if (studentAnswer && studentAnswer.toLowerCase().trim() === q.answer.toLowerCase().trim()) {
          questionMarks[q.id] = q.marks;
          totalMarks += q.marks;
        } else {
          questionMarks[q.id] = 0;
        }
      } else if (q.type === 'short' || q.type === 'discussion') {
        // Keyword matching for subjective questions (partial credit)
        if (studentAnswer) {
          const answerLower = studentAnswer.toLowerCase();
          const matchedKeywords = q.keywords.filter(keyword => 
            answerLower.includes(keyword.toLowerCase())
          );
          const keywordScore = (matchedKeywords.length / q.keywords.length) * q.marks;
          questionMarks[q.id] = Math.round(keywordScore * 10) / 10; // Round to 1 decimal
          totalMarks += questionMarks[q.id];
        } else {
          questionMarks[q.id] = 0;
        }
      }
    });

    return {
      totalMarks: Math.round(totalMarks * 10) / 10,
      questionMarks,
      percentage: Math.round((totalMarks / answerKey.totalMarks) * 100)
    };
  };

  const handleAutoMark = (submission, assessment) => {
    const result = autoMarkSubmission(submission, assessment);
    if (result) {
      const submissionId = `${submission.studentId}-${assessment.id}`;
      setSubmissions(prev => ({
        ...prev,
        [submissionId]: {
          ...prev[submissionId],
          autoMarked: true,
          totalMarks: result.totalMarks,
          questionMarks: result.questionMarks,
          percentage: result.percentage,
          markedBy: currentUser?.name || 'Auto-Grading System',
          markedAt: new Date(),
        }
      }));
      
      // Save marks to Grade Book via SyncContext
      saveAssessmentMark(
        submission.studentId,
        assessment.id,
        result.totalMarks,
        assessment.maxMarks,
        true // autoMarked
      );
      
      // Also add to change log
      addChange('update_assessment_marks', {
        studentId: submission.studentId,
        assessmentId: assessment.id,
        marks: result.totalMarks,
        maxMarks: assessment.maxMarks,
      });
      
      toast.success(`Auto-marked: ${submission.studentName} - ${assessment.title}`);
    }
  };

  const handleManualMark = () => {
    if (!currentSubmission) return;

    // Use the submission's own assessment ID, not the dropdown selection
    const submissionAssessmentId = currentSubmission.assessmentId;
    const submissionAssessment = assessments.find(a => a.id === submissionAssessmentId);
    
    if (!submissionAssessment) return;

    const submissionId = `${currentSubmission.studentId}-${submissionAssessmentId}`;
    // Handle both per-question marks and total marks
    const totalMarks = marks['total'] !== undefined 
      ? parseFloat(marks['total']) || 0
      : Object.values(marks).reduce((sum, mark) => sum + (parseFloat(mark) || 0), 0);
    
    setSubmissions(prev => ({
      ...prev,
      [submissionId]: {
        ...prev[submissionId],
        autoMarked: false,
        totalMarks: totalMarks,
        questionMarks: marks,
        percentage: Math.round((totalMarks / submissionAssessment.maxMarks) * 100),
        markedBy: currentUser?.name || 'Teacher',
        markedAt: new Date(),
      }
    }));

    // Save marks to Grade Book via SyncContext - use submission's assessment ID
    saveAssessmentMark(
      currentSubmission.studentId,
      submissionAssessmentId,
      totalMarks,
      submissionAssessment.maxMarks,
      false // manually marked
    );

    // Also add to change log
    addChange('update_assessment_marks', {
      studentId: currentSubmission.studentId,
      assessmentId: submissionAssessmentId,
      marks: totalMarks,
      maxMarks: submissionAssessment.maxMarks,
    });

    toast.success(`Marked: ${currentSubmission.studentName} - ${submissionAssessment.title}`);
    
    setMarkingDialogOpen(false);
    setCurrentSubmission(null);
    setMarks({});
  };

  const openMarkingDialog = (submission) => {
    setCurrentSubmission(submission);
    setMarks(submission.questionMarks || {});
    setMarkingDialogOpen(true);
  };

  const getSubmissionsForAssessment = () => {
    if (!selectedAssessment) return [];
    return Object.values(submissions).filter(
      s => s.assessmentId === selectedAssessment.id && s.status === 'submitted'
    );
  };

  const submissionsList = getSubmissionsForAssessment();
  const submittedCount = submissionsList.length;
  const markedCount = submissionsList.filter(s => s.totalMarks !== null).length;
  const pendingCount = submittedCount - markedCount;

  // Reset All Marks Handler
  const handleResetAllMarks = () => {
    localStorage.removeItem('assessmentMarks');
    toast.success('All assessment marks have been reset!');
    window.location.reload();
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading classes...</p>
      </div>
    );
  }

  // Show empty state
  if (teacherClasses.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">No classes assigned to you.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-fuchsia-50 to-pink-50 -m-6 p-6">
      <div className="space-y-6 animate-in fade-in duration-500">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <FileCheck className="h-8 w-8 text-primary" />
              Assessment Submissions & Marking
            </h1>
            <p className="text-muted-foreground mt-1">
              Review student submissions, auto-mark objective questions, and sync marks to Grade Book
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              className="gap-2"
              onClick={() => navigate('/dashboard/gradebook')}
            >
              <ExternalLink className="h-4 w-4" />
              Open Grade Book
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="gap-2 border-red-200 text-red-600 hover:bg-red-50">
                  <RotateCcw className="h-4 w-4" />
                  Reset Marks
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Reset All Assessment Marks?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will clear all saved marks and reset testing data. This action is useful for testing but cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleResetAllMarks} className="bg-red-600 hover:bg-red-700">
                    Yes, Reset All Marks
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Select Class</label>
                <Select value={selectedClass} onValueChange={setSelectedClass}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {teacherClasses.map((cls) => (
                      <SelectItem key={cls.id} value={cls.name}>{cls.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Select Assessment</label>
                <Select 
                  value={selectedAssessment?.id} 
                  onValueChange={(val) => setSelectedAssessment(assessments.find(a => a.id === val))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {assessments.map(assessment => (
                      <SelectItem key={assessment.id} value={assessment.id}>
                        {assessment.title} ({assessment.maxMarks} marks)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Submissions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">{submittedCount}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Marked</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">{markedCount}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Pending</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-600">{pendingCount}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-600">
                {submittedCount > 0 ? Math.round((markedCount / submittedCount) * 100) : 0}%
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Submissions Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Student Submissions</CardTitle>
                <CardDescription className="mt-1">
                  {selectedAssessment ? `${selectedAssessment.title} - ${selectedAssessment.maxMarks} marks` : 'Select an assessment'}
                </CardDescription>
              </div>
              {selectedAssessment && answerKeys[selectedAssessment.id] && (
                <Button
                  onClick={() => {
                    submissionsList.filter(s => !s.totalMarks).forEach(s => {
                      handleAutoMark(s, selectedAssessment);
                    });
                  }}
                  className="gap-2"
                  disabled={pendingCount === 0}
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Auto-Mark All Pending ({pendingCount})
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border overflow-hidden shadow-md">
              <Table>
                <TableHeader className="bg-gradient-to-r from-purple-100 to-pink-100">
                  <TableRow>
                    <TableHead className="font-bold">Student Name</TableHead>
                    <TableHead className="font-bold">Assessment</TableHead>
                    <TableHead className="font-bold text-center">Submitted At</TableHead>
                    <TableHead className="font-bold text-center">Status</TableHead>
                    <TableHead className="font-bold text-center">Marks</TableHead>
                    <TableHead className="font-bold text-center">Percentage</TableHead>
                    <TableHead className="font-bold text-center">Marked By</TableHead>
                    <TableHead className="text-right font-bold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {submissionsList.map((submission) => {
                    const submissionId = `${submission.studentId}-${submission.assessmentId}`;
                    const submissionAssessment = assessments.find(a => a.id === submission.assessmentId);
                    return (
                      <TableRow key={submissionId} className="hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50">
                        <TableCell className="font-medium">{submission.studentName}</TableCell>
                        <TableCell className="text-sm font-medium text-blue-700">
                          {submissionAssessment?.title || submission.assessmentTitle}
                          <div className="text-xs text-muted-foreground">Max: {submissionAssessment?.maxMarks || '?'} marks</div>
                        </TableCell>
                        <TableCell className="text-center text-xs">
                          {submission.submittedAt.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-center">
                          {submission.totalMarks !== null ? (
                            <Badge className="bg-green-100 text-green-700 border-green-200">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Marked
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-orange-50 border-orange-200 text-orange-700">
                              <Clock className="h-3 w-3 mr-1" />
                              Pending
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center font-mono font-bold">
                          {submission.totalMarks !== null ? `${submission.totalMarks}/${selectedAssessment?.maxMarks}` : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          {submission.percentage !== null ? (
                            <Badge variant="outline" className={`${
                              submission.percentage >= 80 ? 'bg-green-50 text-green-700 border-green-200' :
                              submission.percentage >= 60 ? 'bg-blue-50 text-blue-700 border-blue-200' :
                              submission.percentage >= 40 ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                              'bg-red-50 text-red-700 border-red-200'
                            }`}>
                              {submission.percentage}%
                            </Badge>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-center text-xs">
                          {submission.markedBy || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openMarkingDialog(submission)}
                            >
                              <Edit className="h-3 w-3 mr-1" />
                              {submission.totalMarks !== null ? 'Review' : 'Mark'}
                            </Button>
                            {selectedAssessment && answerKeys[selectedAssessment.id] && !submission.totalMarks && (
                              <Button
                                size="sm"
                                onClick={() => {
                                  const subAssessment = assessments.find(a => a.id === submission.assessmentId);
                                  if (subAssessment) handleAutoMark(submission, subAssessment);
                                }}
                              >
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Auto
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Marking Dialog */}
        <Dialog open={markingDialogOpen} onOpenChange={setMarkingDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5" />
                Marking: {currentSubmission?.studentName}
              </DialogTitle>
              <DialogDescription>
                {(() => {
                  const submissionAssessment = assessments.find(a => a.id === currentSubmission?.assessmentId);
                  return `Assessment: ${submissionAssessment?.title || 'N/A'} (Max Marks: ${submissionAssessment?.maxMarks || '?'})`;
                })()}
              </DialogDescription>
            </DialogHeader>

            {currentSubmission && selectedAssessment && (
              <div className="space-y-4">
                {answerKeys[selectedAssessment.id] ? (
                  answerKeys[selectedAssessment.id].questions.map((q, idx) => {
                  const studentAnswer = currentSubmission.answers[q.id];
                  const isCorrect = q.type === 'mcq' || q.type === 'fill' || q.type === 'tf' 
                    ? studentAnswer?.toLowerCase().trim() === q.answer.toLowerCase().trim()
                    : null;

                  return (
                    <Card key={q.id} className={`border-l-4 ${
                      isCorrect === true ? 'border-l-green-500' : 
                      isCorrect === false ? 'border-l-red-500' : 
                      'border-l-blue-500'
                    }`}>
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-sm">
                            Question {idx + 1} ({q.marks} marks) - {q.type.toUpperCase()}
                          </CardTitle>
                          {isCorrect !== null && (
                            isCorrect ? 
                              <Badge className="bg-green-100 text-green-700"><Check className="h-3 w-3 mr-1" /> Correct</Badge> :
                              <Badge className="bg-red-100 text-red-700"><X className="h-3 w-3 mr-1" /> Incorrect</Badge>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {/* Student Answer */}
                        <div className="bg-blue-50 rounded-lg p-3">
                          <p className="text-xs font-semibold text-blue-900 mb-1">STUDENT ANSWER:</p>
                          <p className="text-sm">{studentAnswer || '(No answer provided)'}</p>
                        </div>

                        {/* Correct Answer */}
                        <div className="bg-green-50 rounded-lg p-3">
                          <p className="text-xs font-semibold text-green-900 mb-1">CORRECT ANSWER:</p>
                          <p className="text-sm">
                            {q.type === 'mcq' || q.type === 'fill' || q.type === 'tf' ? q.answer : 
                             `Keywords: ${q.keywords.join(', ')}`}
                          </p>
                        </div>

                        {/* Mark Input */}
                        <div className="flex items-center gap-2">
                          <label className="text-sm font-medium">Award Marks:</label>
                          <Input
                            type="number"
                            value={marks[q.id] || ''}
                            onChange={(e) => setMarks(prev => ({ ...prev, [q.id]: e.target.value }))}
                            max={q.marks}
                            min={0}
                            step={0.5}
                            className="w-20"
                          />
                          <span className="text-sm text-muted-foreground">/ {q.marks}</span>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
                ) : (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground mb-4">No answer key available for this assessment.</p>
                    <p className="text-sm">You can still enter marks manually:</p>
                    <div className="mt-4 space-y-2">
                      <Input
                        type="number"
                        placeholder="Enter total marks"
                        value={marks['total'] || ''}
                        onChange={(e) => setMarks({ total: e.target.value })}
                        max={selectedAssessment?.maxMarks}
                        min={0}
                        className="max-w-xs mx-auto"
                      />
                      <p className="text-sm text-muted-foreground">/ {selectedAssessment?.maxMarks}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <div className="flex items-center justify-between w-full">
                <div className="text-sm">
                  <span className="font-semibold">Total Marks: </span>
                  <span className="text-lg font-bold text-primary">
                    {Object.values(marks).reduce((sum, m) => sum + (parseFloat(m) || 0), 0)} / {selectedAssessment?.maxMarks}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setMarkingDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleManualMark} className="gap-2">
                    <Save className="h-4 w-4" />
                    Save Marks & Sync to Grade Book
                  </Button>
                </div>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
